/**
 * Precedence between the termination modal's two dates.
 *
 * Last Vesting Date is the primary as-of date for grants: every backend path that settles one
 * reads it first and falls back to the termination date only when it is unset
 * (`resolveStakeholderTerminationAsOfDate`). The reverse-vesting repurchase follows it only once the
 * operator picked it (`resolveReverseVestingAsOfDate`) — a derived date is a grant trigger. It
 * decides the vested/unvested split, the final exercise date, and possibly the repurchase figures,
 * so the modal must never quietly move it.
 *
 * Kept out of the component so the ownership rule can be asserted directly, instead of through a
 * rendered modal driving three in-flight projections.
 */

/**
 * True when the derived date the backend just returned may be written into the form.
 *
 * The derivation is a convenience — it fills the field in from the termination date for an
 * operator who has not picked one. It is not allowed to take the field back afterwards, whatever
 * the termination date later becomes, and including a date deliberately set after it.
 */
export const shouldApplyDerivedLastVestingDate = ({
  currentMs,
  derivedMs,
  isManual,
  isStale,
}: {
  /** The value the form holds *now*, not the one the request was issued with. */
  currentMs: null | number;
  derivedMs: null | number;
  isManual: boolean;
  /** True once a newer projection has been issued for this stakeholder. */
  isStale: boolean;
}): boolean => {
  // A superseded response carries the previous termination date's answer. Writing it would drop
  // the newer one on arrival — and these resolve in network order, not in the order issued.
  if (isStale) return false;
  // The operator owns the field from the moment they pick a date.
  if (isManual) return false;
  if (derivedMs == null) return false;
  // Re-writing the value the form already holds costs a render and re-fires both projections.
  return derivedMs !== currentMs;
};
